import { UIConstants } from '../../shared/models/Constants';
import { RoleEligibilityEntry } from './MyAccessRequests.types';

export interface ApprovedAccessEntry {
    roleDefinitionId?: string;
    endDate?: string;
    allowRemove?: boolean;
}

export interface CurrentAccessResult {
    currentAccessMap: { [roleId: string]: string };
    approvedAccesses: ApprovedAccessEntry[];
    eligibilityMap: { [roleId: string]: RoleEligibilityEntry };
}

export function buildMercuryCurrentAccess(approved: any[]): CurrentAccessResult {
    const hasPnL = approved.some((e: any) => e.PermissionGroup?.includes('P&L'));
    const hasBasicBlocker = approved.some((e: any) =>
        e.PermissionGroup?.includes('P&L') || e.PermissionLevel?.includes('Basic'));

    const eligibilityMap: { [roleId: string]: RoleEligibilityEntry } = {};
    const approvedAccesses = approved.map((entry: any) => {
        const roleId = entry.RoleDefinitionId ?? entry?.role?.RoleId;
        const roleName = entry.PermissionLevel !== entry.PermissionGroup
            ? `${entry.PermissionGroup}-${entry.PermissionLevel}`
            : entry.PermissionLevel;

        if (roleId) {
            eligibilityMap[roleId] = {
                allowEdit: !!entry.AllowEdit,
                roleAllowRenew: !!entry.AllowRenew,
            };
        }

        const isRemovable = !(
            (roleName === UIConstants.PermissionTypes.MercuryInforma && hasBasicBlocker)
            || (roleName === 'Basic Access' && hasPnL)
        );

        return {
            roleDefinitionId: roleId,
            endDate: entry.EndDate ?? entry.endDate,
            allowRemove: isRemovable,
        };
    });

    // Mercury does not use currentAccessMap (role assignment IDs come from the approved access directly).
    // Returning an empty map is intentional — consumers must use approvedAccesses for Mercury lifecycle actions.
    return { currentAccessMap: {}, approvedAccesses, eligibilityMap };
}

export function buildNonMercuryApprovedAccesses(approved: any[]): {
    currentAccessMap: { [roleId: string]: string };
    approvedAccesses: ApprovedAccessEntry[];
} {
    const currentAccessMap: { [roleId: string]: string } = {};
    for (const entry of approved) {
        const roleId = entry?.role?.roleId ?? entry?.roleDefinitionId;
        const assignmentId = entry?.id;
        if (roleId && assignmentId) {
            currentAccessMap[roleId] = assignmentId;
        }
    }

    const approvedAccesses = approved.map((entry: any) => ({
        roleDefinitionId: entry?.role?.roleId ?? entry?.roleDefinitionId,
        endDate: entry?.endDate,
        allowRemove: true,
    }));

    return { currentAccessMap, approvedAccesses };
}

export function buildNonMercuryEligibilityMap(
    approved: any[],
    roleData: any[],
    isExternalAADTenant: boolean
): { [roleId: string]: RoleEligibilityEntry } {
    const eligibilityMap: { [roleId: string]: RoleEligibilityEntry } = {};
    for (const entry of approved) {
        const roleId = entry.role?.roleId ?? entry.roleDefinitionId;
        const roleDefId = entry.role?.roleDefinitionId ?? entry.roleDefinitionId;
        const selectedRole = roleData.find((r: any) => r.id === roleDefId || r.id === roleId);
        if (selectedRole && roleId) {
            const actions: string[] = selectedRole.permissions?.[0]?.actions || [];
            eligibilityMap[roleId] = {
                allowEdit: isExternalAADTenant
                    ? false
                    : actions.some(a => a.indexOf(UIConstants.RoleDefinition.AllowEditFlag) > -1),
                roleAllowRenew: actions.some(a => a.indexOf(UIConstants.RoleDefinition.AllowRenewFlag) > -1),
            };
        }
    }
    return eligibilityMap;
}
